import { useStore } from "@nanostores/solid";
import { Link, useRouterState } from "@tanstack/solid-router";
import { Show } from "solid-js";
import { IconLogo } from "~/components/icons";
import { MobileNav } from "~/components/mobile-nav";
import { ModeToggle } from "~/components/mode-toggle";
import { authClient } from "~/lib/auth-client";

export default function Navbar() {
  const session = useStore(authClient.useSession);
  const routerState = useRouterState();

  // Use `resolvedLocation` (the committed location) instead of `location`.
  // `location` updates as soon as a navigation *starts*, which would flip the
  // navbar to the light theme while the old page (e.g. the dark home hero) is
  // still rendered — causing a white flash during slow transitions (notably
  // after a hard refresh when route chunks aren't cached yet).
  const isHome = () =>
    (routerState().resolvedLocation ?? routerState().location).pathname === "/";

  return (
    <header
      class={
        isHome()
          ? "absolute left-0 top-0 z-40 h-16 bg-transparent transition-colors duration-200"
          : "sticky left-0 top-0 z-40 h-16 border-b bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/85"
      }
      style={{ right: "var(--scrollbar-width, 0px)" }}
    >
      <div class="relative z-10 mx-auto flex h-full w-full max-w-7xl items-center px-8">
        {/* Logo */}
        <Link to="/" class="flex items-center gap-3 shrink-0">
          <IconLogo
            class="size-9"
            fill={isHome() ? "#ffffff" : "currentColor"}
          />
          <div class="flex flex-col justify-center">
            <span
              class={
                isHome()
                  ? "text-base font-semibold leading-none tracking-tight text-white"
                  : "text-base font-semibold leading-none tracking-tight text-foreground"
              }
            >
              ActShark
            </span>
            <span
              class={
                isHome()
                  ? "mt-1 text-[10px] font-medium uppercase leading-none tracking-[0.2em] text-white/60"
                  : "mt-1 text-[10px] font-medium uppercase leading-none tracking-[0.2em] text-muted-foreground"
              }
            >
              accounting
            </span>
          </div>
        </Link>

        {/* Right actions */}
        <div class="ml-auto flex items-center gap-3.5">
          <Show when={!isHome()}>
            <Link
              to="/"
              class="hidden md:inline-flex items-center justify-center h-8 px-4 text-xs font-bold uppercase tracking-wide rounded-full border border-border text-foreground transition-colors duration-200 hover:bg-accent hover:text-accent-foreground"
            >
              Home
            </Link>
          </Show>
          {/* Sign In / Sign Up or Dashboard */}
          <Show
            when={session().data}
            fallback={
              <>
                <Link
                  to="/login"
                  class={
                    isHome()
                      ? "hidden md:inline-flex items-center justify-center h-8 px-4 text-xs font-bold uppercase tracking-wide rounded-full border border-white/20 text-white transition-colors duration-200 hover:border-white/40"
                      : "hidden md:inline-flex items-center justify-center h-8 px-4 text-xs font-bold uppercase tracking-wide rounded-full border border-border text-foreground transition-colors duration-200 hover:bg-accent hover:text-accent-foreground"
                  }
                >
                  Sign In
                </Link>
                <Link
                  to="/login"
                  search={{ mode: "register" }}
                  class={
                    isHome()
                      ? "hidden md:inline-flex items-center justify-center h-8 px-4 text-xs font-bold uppercase tracking-wide rounded-full bg-white/10 border border-white/20 text-white transition-colors duration-200 hover:bg-white/20 hover:border-white/40"
                      : "hidden md:inline-flex items-center justify-center h-8 px-4 text-xs font-bold uppercase tracking-wide rounded-full bg-primary text-primary-foreground transition-colors duration-200 hover:bg-primary/90"
                  }
                >
                  Sign Up
                </Link>
              </>
            }
          >
            <button
              type="button"
              onClick={() => authClient.signOut()}
              class={
                isHome()
                  ? "hidden md:inline-flex items-center justify-center h-8 px-4 text-xs font-bold uppercase tracking-wide rounded-full border border-white/20 text-white transition-colors duration-200 hover:border-white/40"
                  : "hidden md:inline-flex items-center justify-center h-8 px-4 text-xs font-bold uppercase tracking-wide rounded-full border border-border text-foreground transition-colors duration-200 hover:bg-accent hover:text-accent-foreground"
              }
            >
              Sign Out
            </button>
            <Link
              to="/dashboard"
              class={
                isHome()
                  ? "hidden md:inline-flex items-center justify-center h-8 px-4 text-xs font-bold uppercase tracking-wide rounded-full bg-white/10 border border-white/20 text-white transition-colors duration-200 hover:bg-white/20 hover:border-white/40"
                  : "hidden md:inline-flex items-center justify-center h-8 px-4 text-xs font-bold uppercase tracking-wide rounded-full bg-primary text-primary-foreground transition-colors duration-200 hover:bg-primary/90"
              }
            >
              Dashboard
            </Link>
          </Show>
          {/* Mobile nav */}
          <div class="flex items-center -mr-1.5">
            <Show when={!isHome()}>
              <ModeToggle />
            </Show>
            <div class="md:hidden">
              <MobileNav tone={isHome() ? "light" : "dark"} />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
